import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useContext } from 'react';
import watermarkImage from '../assets/coverzone-watermark.png';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

interface Claim {
  id: string;
  created_date: string;
  updated_date?: string;
  reference_number: string;
  client_name: string;
  client_id?: string;
  status: string;
  consultant_name: string;
  claim_type: string;
  description: string;
}

interface Consultant {
  id: string;
  name: string;
  surname: string;
  email: string;
}

interface Client {
  id: string;
  name: string;
  surname: string;
  email: string;
  client_type?: string;
  contact_number?: string;
  mobile_number?: string;
  telephone_number?: string;
  organization?: string;
  personal?: boolean;
  commercial?: boolean;
  medical?: boolean;
  client_number?: string;
}

interface CreateClaimForm {
  client_type: string;
  client_id: string;
  client_name: string;
  claim_type: string;
  claim_method: string;
  description: string;
  consultant_id?: string;
}

const ManageClaims = () => {
  const { can } = usePermissions();
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string } | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClaims, setFilteredClaims] = useState<Claim[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchClaimTerm, setSearchClaimTerm] = useState('');
  const [searchConsultantTerm, setSearchConsultantTerm] = useState('');
  const [currentUserFullName, setCurrentUserFullName] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [createFormData, setCreateFormData] = useState<CreateClaimForm>({
    client_type: '',
    client_id: '',
    client_name: '',
    claim_type: '',
    claim_method: '',
    description: ''
  });
  const [createFormErrors, setCreateFormErrors] = useState<Partial<CreateClaimForm>>({});
  const [createSuccessMessage, setCreateSuccessMessage] = useState<string | null>(null);
  const [assignToDifferentConsultant, setAssignToDifferentConsultant] = useState(false);
  const [consultantSearchTerm, setConsultantSearchTerm] = useState('');
  const [filteredConsultants, setFilteredConsultants] = useState<Consultant[]>([]);
  const [selectedConsultantId, setSelectedConsultantId] = useState<string>('');
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        
        const userData = response.data;
        const fullName = userData.username || userData.email || '';
        if (fullName) {
          setCurrentUserFullName(fullName);
          setSearchConsultantTerm(fullName);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch claims
  useEffect(() => {
    const fetchClaims = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_URL}/api/manage/claims`, {
  headers: {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  },
});
        
        if (response.data.success) {
          const claimsData = response.data.data || [];
          // Filter to only show Open claims (not completed)
          const openClaims = claimsData.filter(
            (claim: any) => claim.status?.toLowerCase() !== 'completed'
          );
          setClaims(openClaims);
          setFilteredClaims(openClaims);
        } else {
          throw new Error(response.data.message || 'Failed to fetch claims');
        }
      } catch (error: any) {
        console.error('❌ Error fetching claims:', error);
        setError(error.response?.data?.message || 'Failed to load claims');
        
        const mockClaims: Claim[] = [
          {
            id: '1',
            created_date: '2024-01-15',
            updated_date: '2024-01-15',
            reference_number: 'CLM-001',
            client_name: 'Sample Client',
            client_id: '1',
            status: 'Open',
            consultant_name: 'Sample Consultant',
            claim_type: 'Total Loss',
            description: 'Sample claim description'
          }
        ];
        setClaims(mockClaims);
        setFilteredClaims(mockClaims);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClaims();
  }, []);

  // Fetch consultants
  useEffect(() => {
    const fetchConsultants = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_URL}/api/consultants`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.data.success) {
          const consultantsData = response.data.data || [];
          setConsultants(consultantsData);
        }
      } catch (error: any) {
        console.error('❌ Error fetching consultants:', error);
      }
    };

    fetchConsultants();
  }, []);

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_URL}/api/clients`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (Array.isArray(response.data)) {
          const clientsData = response.data.map((client: any) => ({
            id: client.id.toString(),
            name: client.name || '',
            surname: client.surname || '',
            email: client.email || '',
            client_type: client.personal ? 'Personal Client' : client.commercial ? 'Commercial Client' : 'Unknown',
            contact_number: client.mobile_number || client.telephone_number || '',
            mobile_number: client.mobile_number || '',
            telephone_number: client.telephone_number || '',
            organization: client.organization || '',
          }));
          
          setClients(clientsData);
          setFilteredClients(clientsData);
        } else {
          throw new Error('Unexpected response format from clients endpoint');
        }
      } catch (error: any) {
        console.error('❌ Error fetching clients:', error);
        
        const mockClients: Client[] = [
          {
            id: '1',
            name: 'John',
            surname: 'Doe',
            email: 'john.doe@example.com',
            client_type: 'Personal Client',
            contact_number: '+1234567890',
            mobile_number: '+1234567890',
            organization: 'ABC Corp'
          },
          {
            id: '2',
            name: 'Jane',
            surname: 'Smith',
            email: 'jane.smith@example.com',
            client_type: 'Commercial Client',
            contact_number: '+1234567891',
            mobile_number: '+1234567891',
            organization: 'XYZ Ltd'
          }
        ];
        
        setClients(mockClients);
        setFilteredClients(mockClients);
        setError('Using demo client data - Backend clients endpoint returned unexpected format');
      }
    };

    fetchClients();
  }, []);

  // Filter clients based on search term
  useEffect(() => {
    if (clientSearchTerm.trim()) {
      const lowercasedSearch = clientSearchTerm.toLowerCase();
      const filtered = clients.filter(client =>
        client.name?.toLowerCase().includes(lowercasedSearch) ||
        client.surname?.toLowerCase().includes(lowercasedSearch) ||
        client.email?.toLowerCase().includes(lowercasedSearch) ||
        `${client.name} ${client.surname}`.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [clientSearchTerm, clients]);

  // Filter consultants based on search term
  useEffect(() => {
    if (consultantSearchTerm.trim()) {
      const lowercasedSearch = consultantSearchTerm.toLowerCase();
      const filtered = consultants.filter(consultant =>
        consultant.name?.toLowerCase().includes(lowercasedSearch) ||
        consultant.surname?.toLowerCase().includes(lowercasedSearch) ||
        consultant.email?.toLowerCase().includes(lowercasedSearch) ||
        `${consultant.name} ${consultant.surname}`.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredConsultants(filtered);
    } else {
      setFilteredConsultants(consultants);
    }
  }, [consultantSearchTerm, consultants]);

  // Handle create claim form input changes
  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({ ...prev, [name]: value }));
    setCreateFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Handle client selection
  const handleClientSelect = (clientId: string, clientName: string) => {
    setCreateFormData(prev => ({ 
      ...prev, 
      client_id: clientId,
      client_name: clientName 
    }));
    setClientSearchTerm(clientName);
  };

  // Handle consultant selection
  const handleConsultantSelect = (consultantId: string, consultantName: string) => {
    setSelectedConsultantId(consultantId);
    setCreateFormData(prev => ({ ...prev, consultant_id: consultantId }));
    setConsultantSearchTerm(consultantName);
  };

  // Validate create form
  const validateCreateForm = () => {
    const errors: Partial<CreateClaimForm> = {};
    if (!createFormData.client_type) errors.client_type = 'Client type is required';
    if (!createFormData.client_id) errors.client_id = 'Client is required';
    if (!createFormData.claim_type) errors.claim_type = 'Claim type is required';
    if (!createFormData.claim_method) errors.claim_method = 'Claim method is required';
    if (!createFormData.description) errors.description = 'Description is required';
    return errors;
  };

  // Handle create claim submission
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateCreateForm();
    if (Object.keys(errors).length > 0) {
      setCreateFormErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/claims`, createFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 201) {
        const newClaim = response.data.data;
        setClaims(prev => [newClaim, ...prev]);
        setCreateSuccessMessage('Claim created successfully!');
        setIsCreateModalOpen(false);
        setCreateFormData({
          client_type: '',
          client_id: '',
          client_name: '',
          claim_type: '',
          claim_method: '',
          description: ''
        });
        setTimeout(() => setCreateSuccessMessage(null), 3000);
      } else {
        setError(response.data.message || 'Failed to create claim');
      }
    } catch (error: any) {
      console.error('❌ Error creating claim:', error);
      setError(error.response?.data?.message || 'Failed to create claim');
    }
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setCreateFormErrors({});
    setClientSearchTerm('');
    setAssignToDifferentConsultant(false);
    setConsultantSearchTerm('');
    setSelectedConsultantId('');
  };

  // Search and filter
  useEffect(() => {
    let filtered = claims;

    if (searchClaimTerm.trim()) {
      const lowercasedSearch = searchClaimTerm.toLowerCase().trim();
      filtered = filtered.filter(claim =>
        claim.reference_number?.toLowerCase().includes(lowercasedSearch) ||
        claim.claim_type?.toLowerCase().includes(lowercasedSearch) ||
        claim.description?.toLowerCase().includes(lowercasedSearch) ||
        claim.client_name?.toLowerCase().includes(lowercasedSearch) ||
        'open'.includes(lowercasedSearch)
      );
    }

    if (searchConsultantTerm.trim()) {
      const lowercasedSearch = searchConsultantTerm.toLowerCase().trim();
      filtered = filtered.filter(claim => {
        const claimConsultantName = claim.consultant_name?.toLowerCase().trim() || '';
        return claimConsultantName.includes(lowercasedSearch);
      });
    }

    setFilteredClaims(filtered);
  }, [searchClaimTerm, searchConsultantTerm, claims]);

  const handleLogout = () => {
    authContext?.setUser(null);
    localStorage.removeItem('token');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    //console.log(`${type} image loaded successfully`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

    if (!can('claims', 'read')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to view Claims.</p>
      </div>
    );
  }

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">

      {/* Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      {/* Sidebar */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header
          title="Claims"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
            </div>
          )}
          {createSuccessMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
              {createSuccessMessage}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Claims</h2>
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                onClick={handleOpenCreateModal}
              >
                Create Claim
              </button>
            </div>

            {/* Search Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Claims
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by reference, type, description, client, or status..."
                    value={searchClaimTerm}
                    onChange={(e) => setSearchClaimTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Consultant
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by consultant name, surname, or email..."
                    value={searchConsultantTerm}
                    onChange={(e) => setSearchConsultantTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {searchConsultantTerm && (
                    <button
                      onClick={() => setSearchConsultantTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search Results Info */}
            {(searchClaimTerm || searchConsultantTerm) && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Showing {filteredClaims.length} of {claims.length} claims
                  {searchClaimTerm && ` matching "${searchClaimTerm}"`}
                  {searchConsultantTerm && ` for consultant "${searchConsultantTerm}"`}
                </p>
              </div>
            )}

            {/* Claims List */}
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-700">Loading claims...</p>
              </div>
            ) : filteredClaims.length === 0 ? (
              <div className="text-center py-8">
                {searchClaimTerm || searchConsultantTerm ? (
                  <p className="text-gray-700">No claims found matching your search criteria.</p>
                ) : (
                  <p className="text-gray-700">No claims available.</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ref No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee / Consultant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClaims.map((claim) => (
                      <tr key={claim.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(claim.created_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(claim.updated_date || claim.created_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              if (claim.client_id) {
                                navigate(`/personal/${claim.client_id}`, { 
                                  state: { 
                                    activeTab: 'claims',
                                    claimId: claim.id 
                                  } 
                                });
                              } else {
                                alert(`Cannot navigate to claim details. No client associated with this claim.`);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {claim.reference_number && claim.reference_number !== 'N/A' 
                              ? claim.reference_number 
                              : `CLM-${String(claim.id).padStart(3, '0')}`}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {claim.client_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate" title={claim.description}>
                            {claim.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {claim.consultant_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Open
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Claim Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New Claim</h2>
            <form onSubmit={handleCreateSubmit}>
              <div className="space-y-4">
                {/* Select Client Type */}
                <div>
                  <label className="block font-medium mb-1">Select Client Type *</label>
                  <select
                    name="client_type"
                    value={createFormData.client_type}
                    onChange={handleCreateInputChange}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select client type</option>
                    <option value="Personal Client">Personal Client</option>
                    <option value="Commercial Client">Commercial Client</option>
                  </select>
                  {createFormErrors.client_type && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.client_type}</p>
                  )}
                </div>

                {/* Find Client */}
                <div>
                  <label className="block font-medium mb-1">Find Client *</label>
                  <input
                    type="text"
                    placeholder="Type to search for clients..."
                    value={createFormData.client_id ? createFormData.client_name : clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    className="w-full border p-2 rounded"
                  />
                  {createFormData.client_id && (
                    <p className="text-green-600 text-sm mt-1">Client selected: {createFormData.client_name}</p>
                  )}
                  {createFormErrors.client_id && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.client_id}</p>
                  )}
                  
                  {/* Client Search Results */}
                  {clientSearchTerm && !createFormData.client_id && filteredClients.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border rounded">
                      {filteredClients.map(client => (
                        <div
                          key={client.id}
                          className="p-2 hover:bg-gray-100 cursor-pointer border-b"
                          onClick={() => handleClientSelect(client.id, `${client.name} ${client.surname}`)}
                        >
                          <div className="font-medium">{client.name} {client.surname}</div>
                          <div className="text-sm text-gray-600">{client.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Select Claim Type */}
                <div>
                  <label className="block font-medium mb-1">Select Claim Type *</label>
                  <select
                    name="claim_type"
                    value={createFormData.claim_type}
                    onChange={handleCreateInputChange}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select claim type</option>
                    <option value="Total Loss">Total Loss</option>
                    <option value="Partial Loss">Partial Loss</option>
                  </select>
                  {createFormErrors.claim_type && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.claim_type}</p>
                  )}
                </div>

                {/* Select Claim Method */}
                <div>
                  <label className="block font-medium mb-1">Select Claim Method *</label>
                  <select
                    name="claim_method"
                    value={createFormData.claim_method}
                    onChange={handleCreateInputChange}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select claim method</option>
                    <option value="Email">Email</option>
                    <option value="Phone Call">Phone Call</option>
                    <option value="SMS">SMS</option>
                    <option value="Whatsapp">Whatsapp</option>
                    <option value="Face To Face">Face To Face</option>
                  </select>
                  {createFormErrors.claim_method && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.claim_method}</p>
                  )}
                </div>

                {/* Assign to Different Consultant Checkbox */}
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="assignToDifferentConsultant"
                    checked={assignToDifferentConsultant}
                    onChange={(e) => setAssignToDifferentConsultant(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="assignToDifferentConsultant" className="ml-2 block text-sm font-medium text-gray-700">
                    Assign This Claim To A Different Consultant
                  </label>
                </div>

                {/* Search For Consultant Field - Conditionally Rendered */}
                {assignToDifferentConsultant && (
                  <div>
                    <label className="block font-medium mb-1">Search For Consultant *</label>
                    <input
                      type="text"
                      placeholder="Type to search for consultants..."
                      value={consultantSearchTerm}
                      onChange={(e) => setConsultantSearchTerm(e.target.value)}
                      className="w-full border p-2 rounded"
                    />
                    {selectedConsultantId && (
                      <p className="text-green-600 text-sm mt-1">Consultant selected</p>
                    )}
                    
                    {/* Consultant Search Results */}
                    {consultantSearchTerm && !selectedConsultantId && filteredConsultants.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto border rounded">
                        {filteredConsultants.map(consultant => (
                          <div
                            key={consultant.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer border-b"
                            onClick={() => handleConsultantSelect(consultant.id, `${consultant.name} ${consultant.surname}`)}
                          >
                            <div className="font-medium">{consultant.name} {consultant.surname}</div>
                            <div className="text-sm text-gray-600">{consultant.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Clear selection button */}
                    {selectedConsultantId && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedConsultantId('');
                          setCreateFormData(prev => ({ ...prev, consultant_id: '' }));
                          setConsultantSearchTerm('');
                        }}
                        className="text-red-500 text-sm mt-1 underline"
                      >
                        Clear consultant selection
                      </button>
                    )}
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block font-medium mb-1">Description *</label>
                  <textarea
                    name="description"
                    value={createFormData.description}
                    onChange={handleCreateInputChange}
                    className="w-full border p-2 rounded h-32"
                    placeholder="Enter claim description..."
                  />
                  {createFormErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.description}</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setAssignToDifferentConsultant(false);
                    setConsultantSearchTerm('');
                    setSelectedConsultantId('');
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Create Claim
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageClaims;